import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Heading,
  IconArrow,
  IconTick,
  List,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import type { ReactNode } from 'react';

import { useBrandName } from 'src/hooks/useBrandName';
import { useReturnUrl } from 'src/hooks/useReturnUrl';

import translations from './.vocab';
import promoteSignInImageAU from './images/au.jpg';
import promoteSignInImageHK from './images/hk.jpg';
import promoteSignInImageMYID from './images/my_id.jpg';
import promoteSignInImageNZ from './images/nz.jpg';
import promoteSignInImagePHTH from './images/ph_th.jpg';
import promoteSignInImageSG from './images/sg.jpg';

import * as styleRefs from './PromoteSignInSection.css';

const usePromoteSignInImage = () => {
  const { country } = useMelwaysInfo();
  switch (country) {
    case 'au':
      return promoteSignInImageAU;
    case 'nz':
      return promoteSignInImageNZ;
    case 'sg':
      return promoteSignInImageSG;
    case 'my':
    case 'id':
      return promoteSignInImageMYID;
    case 'ph':
    case 'th':
      return promoteSignInImagePHTH;
    case 'hk':
      return promoteSignInImageHK;
    default:
      return promoteSignInImageAU;
  }
};

const PromoteSignInSectionView = ({
  header,
  content,
  imageSrc,
  widerWidget,
}: {
  header: string | ReactNode;
  content: ReactNode;
  imageSrc: string;
  widerWidget?: boolean;
}) => (
  <Box
    display="flex"
    alignItems="center"
    flexDirection={{ mobile: 'column', tablet: 'row' }}
    position="relative"
    gap="large"
    className={styleRefs.rootContainer}
  >
    <Box
      background="surface"
      borderRadius="large"
      paddingY="large"
      paddingX={{ tablet: 'large' }}
      display="flex"
      flexDirection="column"
      gap="large"
      zIndex={1}
      className={[
        styleRefs.widgetContainer,
        {
          [styleRefs.widgetContainerWider]: widerWidget,
        },
      ]}
      position="relative"
    >
      <Heading level="2">{header}</Heading>
      {content}
    </Box>
    <Box
      overflow="hidden"
      position={{ mobile: 'relative', tablet: 'absolute' }}
      right={0}
      height="full"
      borderRadius="large"
      className={styleRefs.imageContainer}
    >
      <Box component="img" src={imageSrc} className={styleRefs.image} alt="" />
    </Box>
  </Box>
);

/**
 * aka v1
 */
export const PromoteSignInSectionWithSellingPoints = () => {
  const { t } = useTranslations(translations);
  const promoteSignInImage = usePromoteSignInImage();
  const signInLink = useReturnUrl({ mode: 'signin' });
  const { language } = useMelwaysInfo();
  const brandName = useBrandName();

  return (
    <PromoteSignInSectionView
      header={t(
        'Looking for the right job? You’re more likely to find it on {brand}',
        { brand: brandName },
      )}
      content={
        <>
          <Stack space="small">
            <Text>{t('Sign in for better job matches:')}</Text>
            <List type="icon" space="small" icon={<IconTick tone="positive" />}>
              <Text>{t('Smarter search to find all the right jobs')}</Text>
              <Text>{t('Job recommendations personalised to you')}</Text>
              <Text>
                {t('Instant job alerts so you never miss an opportunity')}
              </Text>
            </List>
          </Stack>
          <Box style={{ alignSelf: 'start' }}>
            <ButtonLink
              icon={<IconArrow direction="right" />}
              href={signInLink}
              tone="formAccent"
              iconPosition="trailing"
            >
              {t('Get started')}
            </ButtonLink>
          </Box>
        </>
      }
      imageSrc={promoteSignInImage}
      widerWidget={language === 'id'}
    />
  );
};

/**
 * aka v2
 */
export const PromoteSignInSectionWithMultiCtas = () => {
  const { t } = useTranslations(translations);
  const registerLink = useReturnUrl({ mode: 'register' });
  const signInLink = useReturnUrl({ mode: 'signin' });
  const promoteSignInImage = usePromoteSignInImage();
  const brandName = useBrandName();

  return (
    <PromoteSignInSectionView
      header={t('Find the right job for you on {brand}', { brand: brandName })}
      content={
        <>
          <Text>{t('Sign in to your profile for better job matches​')}</Text>
          <ButtonLink tone="formAccent" href={signInLink}>
            {t('Sign in')}
          </ButtonLink>
          <Text>
            {t('Don’t have an account? Register', {
              TextLink: (children) => (
                <TextLink href={registerLink}>{children}</TextLink>
              ),
            })}
          </Text>
        </>
      }
      imageSrc={promoteSignInImage}
    />
  );
};
